import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// 레이아웃
import DefaultLayout from '@/components/layout/DefaultLayout.vue'
import AuthLayout from '@/components/layout/AuthLayout.vue'

// 페이지
import HomePage from '@/views/HomePage.vue'
import LoginPage from '@/views/auth/LoginPage.vue'
import RegisterPage from '@/views/auth/RegisterPage.vue'
import ForgotPasswordPage from '@/views/auth/ForgotPasswordPage.vue'
import PostListPage from '@/views/posts/PostListPage.vue'
import PostDetailPage from '@/views/posts/PostDetailPage.vue'
import PostCreatePage from '@/views/posts/PostCreatePage.vue'
import PostEditPage from '@/views/posts/PostEditPage.vue'
import MyPostsPage from '@/views/posts/MyPostsPage.vue'
import MyReservationsPage from '@/views/reservations/MyReservationsPage.vue'
import ProfilePage from '@/views/profile/ProfilePage.vue'
import NoticeListPage from '@/views/notices/NoticeListPage.vue'
import NoticeDetailPage from '@/views/notices/NoticeDetailPage.vue'
import NoticeCreatePage from '@/views/notices/NoticeCreatePage.vue'
import NoticeEditPage from '@/views/notices/NoticeEditPage.vue'
import QnaListPage from '@/views/qna/QnaListPage.vue'
import QnaDetailPage from '@/views/qna/QnaDetailPage.vue'
import QnaCreatePage from '@/views/qna/QnaCreatePage.vue'
import QnaEditPage from '@/views/qna/QnaEditPage.vue'
import AdminDashboard from '@/views/admin/AdminDashboard.vue'
import AdminUsersPage from '@/views/admin/AdminUsersPage.vue'
import NotFoundPage from '@/views/NotFoundPage.vue'

const routes: Array<RouteRecordRaw> = [
  // 인증 관련 페이지 (AuthLayout 사용)
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        path: 'login',
        alias: '/login',
        name: 'Login',
        component: LoginPage,
        meta: {
          title: '로그인',
          requiresAuth: false
        }
      },
      {
        path: 'register',
        alias: '/register',
        name: 'Register',
        component: RegisterPage,
        meta: {
          title: '회원가입',
          requiresAuth: false
        }
      },
      {
        path: 'forgot-password',
        alias: '/forgot-password',
        name: 'ForgotPassword',
        component: ForgotPasswordPage,
        meta: {
          title: '비밀번호 찾기',
          requiresAuth: false
        }
      }
    ]
  },

  // 메인 애플리케이션 (DefaultLayout 사용)
  {
    path: '/',
    component: DefaultLayout,
    children: [
      {
        path: '',
        name: 'Home',
        component: HomePage,
        meta: {
          title: '홈',
          requiresAuth: false
        }
      },
      {
        path: 'posts',
        name: 'Posts',
        component: PostListPage,
        meta: {
          title: '게시글 목록',
          breadcrumbLabel: '예약',
          requiresAuth: false
        }
      },
      {
        path: 'posts/create',
        name: 'PostCreate',
        component: PostCreatePage,
        meta: {
          title: '게시글 작성',
          breadcrumbs: [
            { label: '예약', to: '/posts' },
            { label: '게시글 작성' }
          ],
          requiresAuth: true
        }
      },
      {
        path: 'posts/:id',
        name: 'PostDetail',
        component: PostDetailPage,
        props: true,
        meta: {
          title: '게시글 상세',
          breadcrumbs: [
            { label: '예약', to: '/posts' },
            { label: '게시글 상세', dynamic: true, dynamicKey: 'PostDetail' }
          ],
          requiresAuth: false
        }
      },
      {
        path: 'posts/:id/edit',
        name: 'PostEdit',
        component: PostEditPage,
        props: true,
        meta: {
          title: '게시글 수정',
          breadcrumbs: [
            { label: '예약', to: '/posts' },
            { label: '게시글 수정' }
          ],
          requiresAuth: true
        }
      },
      {
        path: 'my/posts',
        name: 'MyPosts',
        component: MyPostsPage,
        meta: {
          title: '내 게시글',
          requiresAuth: true
        }
      },
      {
        path: 'my/reservations',
        name: 'MyReservations',
        component: MyReservationsPage,
        meta: {
          title: '내 예약',
          requiresAuth: true
        }
      },
      {
        path: 'profile',
        name: 'Profile',
        component: ProfilePage,
        meta: {
          title: '프로필',
          requiresAuth: true
        }
      },
      {
        path: 'notices',
        name: 'Notices',
        component: NoticeListPage,
        meta: {
          title: '공지사항',
          breadcrumbLabel: '공지사항',
          requiresAuth: false
        }
      },
      {
        path: 'notices/create',
        name: 'NoticeCreate',
        component: NoticeCreatePage,
        meta: {
          title: '공지사항 작성',
          breadcrumbs: [
            { label: '공지사항', to: '/notices' },
            { label: '공지사항 작성' }
          ],
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'notices/:id',
        name: 'NoticeDetail',
        component: NoticeDetailPage,
        props: true,
        meta: {
          title: '공지사항 상세',
          breadcrumbs: [
            { label: '공지사항', to: '/notices' },
            { label: '공지사항 상세', dynamic: true, dynamicKey: 'NoticeDetail' }
          ],
          requiresAuth: false
        }
      },
      {
        path: 'notices/:id/edit',
        name: 'NoticeEdit',
        component: NoticeEditPage,
        props: true,
        meta: {
          title: '공지사항 수정',
          breadcrumbs: [
            { label: '공지사항', to: '/notices' },
            { label: '공지사항 수정' }
          ],
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'qna',
        name: 'Qna',
        component: QnaListPage,
        meta: {
          title: 'Q&A',
          breadcrumbLabel: 'Q&A',
          requiresAuth: false
        }
      },
      {
        path: 'qna/create',
        name: 'QnaCreate',
        component: QnaCreatePage,
        meta: {
          title: '질문하기',
          breadcrumbs: [
            { label: 'Q&A', to: '/qna' },
            { label: '질문하기' }
          ],
          requiresAuth: true
        }
      },
      {
        path: 'qna/:id',
        name: 'QnaDetail',
        component: QnaDetailPage,
        props: true,
        meta: {
          title: 'Q&A 상세',
          breadcrumbs: [
            { label: 'Q&A', to: '/qna' },
            { label: 'Q&A 상세', dynamic: true, dynamicKey: 'QnaDetail' }
          ],
          requiresAuth: false
        }
      },
      {
        path: 'qna/:id/edit',
        name: 'QnaEdit',
        component: QnaEditPage,
        props: true,
        meta: {
          title: '질문 수정',
          breadcrumbs: [
            { label: 'Q&A', to: '/qna' },
            { label: '질문 수정' }
          ],
          requiresAuth: true
        }
      }
    ]
  },

  // 관리자 페이지
  {
    path: '/admin',
    component: DefaultLayout,
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    },
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        component: AdminDashboard,
        meta: {
          title: '관리자 대시보드',
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'users',
        name: 'AdminUsers',
        component: AdminUsersPage,
        meta: {
          title: '사용자 관리',
          requiresAuth: true,
          requiresAdmin: true
        }
      }
    ]
  },

  // 404 페이지
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundPage,
    meta: {
      title: '페이지를 찾을 수 없음'
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// 네비게이션 가드
router.beforeEach(async (to) => {
  const authStore = useAuthStore()
  
  // 인증 상태 초기화 (첫 방문 시)
  if (authStore.token && !authStore.user) {
    try {
      await authStore.initialize()
    } catch (error) {
      authStore.logout()
    }
  }

  const requiresAuth = to.matched.some(record => record.meta?.requiresAuth)
  const requiresAdmin = to.matched.some(record => record.meta?.requiresAdmin)
  const isAuthenticated = authStore.isAuthenticated
  const isAdmin = authStore.isAdmin

  // 페이지 제목 설정
  if (to.meta?.title) {
    document.title = `${to.meta.title} - Seatisfy`
  } else {
    document.title = 'Seatisfy'
  }

  // 인증이 필요한 페이지인데 로그인하지 않은 경우
  if (requiresAuth && !isAuthenticated) {
    return { name: 'Login', query: { redirect: to.fullPath }, replace: true }
  }

  // 관리자 권한이 필요한 페이지인데 관리자가 아닌 경우
  if (requiresAdmin && !isAdmin) {
    return { name: 'Home', replace: true }
  }

  // 이미 로그인한 사용자가 로그인/회원가입 페이지에 접근하는 경우
  if (isAuthenticated && (to.name === 'Login' || to.name === 'Register')) {
    return { name: 'Home', replace: true }
  }

  return true
})

export default router
